import React, { useCallback, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { useTodaysAspirations } from '@/services/aspiration/hooks';
import { AspirationsToday } from '@/types.gql';
import { formatDateWithPattern } from '@/utils';

import AspirationContext from '../hooks/useAspirationContext';
import { FARBRO_SITE_ID } from '../utils/constants';
import { initialState } from '../utils/state';
import { AspirationActions } from './AspirationActions';
import { AspirationForm } from './AspirationForm';
import { AspirationGrid, AspirationGridProps } from './AspirationGrid';
import { AspirationRequirements } from './AspirationRequirements';

import './Aspiration.css';

type AspirationProps = {
  className?: string;
  children: React.ReactNode;
};

const Aspiration = ({ className, children }: AspirationProps) => {
  const [params, setParams] = useSearchParams();
  const opuDate =
    params.get('d') || formatDateWithPattern(new Date(), 'MM-dd-yyyy');
  const opuSiteId = params.get('sId');
  const opuTechnicianId = params.get('otId');
  const location = params.get('l');

  const isFarbroSite = opuSiteId === FARBRO_SITE_ID;
  const shouldClearLocation =
    opuSiteId && opuSiteId !== '087071b7-cdb2-e911-818b-005056975fe0';

  if (shouldClearLocation && params.has('l')) {
    const newParams = new URLSearchParams(params);
    newParams.delete('l');
    setParams(newParams);
  }

  const shouldFetchAspirations =
    !!opuSiteId && !!opuDate && (!isFarbroSite || !!location);

  const { data: aspirationsData, refetch: refetchTodaysAspirations } =
    useTodaysAspirations({
      date: shouldFetchAspirations ? opuDate : null,
      siteId: shouldFetchAspirations ? opuSiteId : null,
      techId: opuTechnicianId,
      type: 0,
      location: isFarbroSite ? location : '',
    });

  const [aspDate, setAspDate] = useState(initialState.aspDate);
  const [aspSite, setAspSite] = useState(initialState.aspSite);
  const [opuTech, setOpuTech] = useState(initialState.opuTech);
  const [ovarySideFirst, setOvarySideFirst] = useState<string>('Left');

  const [quickSearchValue, setQuickSearchValue] = useState(
    initialState.quickSearchValue,
  );
  const [selectedAspiration, setSelectedAspiration] = useState(
    initialState.selectedAspiration,
  );
  const [qcDataSubmitted, setQcDataSubmitted] = useState(
    initialState.qcDataSubmitted,
  );

  const stableRefetch = useCallback(() => {
    refetchTodaysAspirations();
  }, [refetchTodaysAspirations]);

  const value = useMemo(
    () => ({
      aspDate,
      setAspDate,
      aspSite,
      setAspSite,
      opuTech,
      setOpuTech,

      quickSearchValue,
      setQuickSearchValue,
      selectedAspiration,
      setSelectedAspiration,
      aspirations: aspirationsData as AspirationsToday[] | undefined,
      refetchTodaysAspirations: stableRefetch,
      qcDataSubmitted,
      setQcDataSubmitted,
      ovarySideFirst,
      setOvarySideFirst,
    }),
    [
      aspDate,
      aspSite,
      aspirationsData,
      stableRefetch,
      opuTech,
      quickSearchValue,
      selectedAspiration,
      qcDataSubmitted,
      ovarySideFirst,
      setOvarySideFirst,
    ],
  );

  return (
    <AspirationContext.Provider value={value}>
      <section className={`aspiration-root ${className}`.trim()}>
        {children}
      </section>
    </AspirationContext.Provider>
  );
};

const Root = Aspiration;
const Actions = AspirationActions;
const Requirements = AspirationRequirements;
const Grid = AspirationGrid;
const Form = AspirationForm;

export {
  Actions,
  Aspiration,
  AspirationActions,
  AspirationGrid,
  AspirationRequirements,
  Form,
  Grid,
  Requirements,
  Root,
};

export type { AspirationGridProps };
